import { type File as KFormFile, Path } from "@ostack.tech/kform";
import { useCallback } from "react";

import { InvalidPathError } from "../utils/errors.ts";
import {
  formatKFormFileAsFileList,
  parseFileListAsKFormFile,
} from "../utils/fileUtils.ts";
import { useLatestValues } from "../utils/useLatestValues.ts";
import type { Controller } from "./useController.ts";
import type { FormatterControllerState } from "./useFormatter.ts";
import {
  type InputOptions,
  type InputOwnController,
  useInput,
} from "./useInput.ts";

/** Options available to the {@link useFileInput} hook. */
export type FileInputOptions<
  T extends KFormFile | null = KFormFile | null,
  TFormatted = FileList,
  TInput = HTMLInputElement,
> = Omit<InputOptions<T, TFormatted, TInput>, "format" | "parse"> &
  FileInputOwnOptions<T, TFormatted, TInput>;

/** Own options available to the {@link useFileInput} hook. */
export interface FileInputOwnOptions<
  T extends KFormFile | null = KFormFile | null,
  TFormatted = FileList,
  TInput = HTMLInputElement,
> {
  formatFromFileList?: (
    fileListValue: FileList,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => TFormatted;
  parseToFileList?: (
    formattedValue: TFormatted,
    state: FormatterControllerState<T, TFormatted>,
    input: TInput | null,
  ) => FileList;
}

/**
 * Controller used to read and control the form value associated with the form
 * input, exposing properties that should be set on the input itself.
 */
export type FileInputController<
  T extends KFormFile | null = KFormFile | null,
  TFormatted = FileList,
  TInput = HTMLInputElement,
> = Controller<T> & FileInputOwnController<TFormatted, TInput>;

/** Own file input controller. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FileInputOwnController<
  TFormatted = FileList,
  TInput = HTMLInputElement,
> extends InputOwnController<TFormatted, TInput> {}

/**
 * Hook controlling the integration between a file value of the form manager and
 * an input of the form.
 *
 * @param path Path of the file form value to access, relative to the current
 *   path.
 * @param options Available options.
 * @returns A controller used to read and control the file form value associated
 *   with the form input, exposing properties that should be set on the input
 *   itself.
 * @throws {InvalidPathError} When the schema at {@link path} is not of file
 *   type.
 */
export function useFileInput<
  T extends KFormFile | null = KFormFile | null,
  TFormatted = File,
  TInput = HTMLInputElement,
>(
  path?: Path | string,
  options?: undefined,
): FileInputController<T, TFormatted, TInput>;
export function useFileInput<
  T extends KFormFile | null = KFormFile | null,
  TFormatted = File,
  TInput = HTMLInputElement,
>(
  path: Path | string | undefined,
  options: FileInputOptions<T, TFormatted, TInput>,
): FileInputController<T, TFormatted, TInput>;
export function useFileInput<
  T extends KFormFile | null = KFormFile | null,
  TFormatted = File,
  TInput = HTMLInputElement,
>(
  path: Path | string = Path.CURRENT,
  {
    formatFromFileList,
    parseToFileList,
    ...options
  }: FileInputOptions<T, TFormatted, TInput> = {},
): FileInputController<T, TFormatted, TInput> {
  const latestOptions = useLatestValues({ parseToFileList });

  // Input options
  const format = useCallback(
    (
      value: T | undefined,
      state: FormatterControllerState<T, TFormatted>,
      input: TInput | null,
    ): TFormatted => {
      const fileList = formatKFormFileAsFileList(value, state.schema);
      return formatFromFileList
        ? formatFromFileList(fileList, state, input)
        : (fileList as TFormatted);
    },
    [formatFromFileList],
  );
  const parse = useCallback(
    (
      formattedValue: TFormatted,
      state: FormatterControllerState<T, TFormatted>,
      input: TInput | null,
    ): T | Promise<T> => {
      return parseFileListAsKFormFile(
        latestOptions.parseToFileList
          ? latestOptions.parseToFileList(formattedValue, state, input)
          : (formattedValue as FileList),
        state.schema,
      );
    },
    [latestOptions],
  );

  const inputController = useInput(path, { format, parse, ...options });

  // Validate schema type
  const { typeInfo } = inputController.useSchema();
  if (typeInfo.name !== "File") {
    throw new InvalidPathError(
      inputController.getState().path,
      "Unsupported schema: Expected schema representing (possibly nullable) " +
        "file values, but got schema representing values of type " +
        `'${typeInfo.toString()}'.`,
    );
  }

  return inputController;
}
